import axios from 'axios';
import { z } from 'zod';

import {
  BILL_SCOUT_RECORDED_TRANSACTIONS_REQUEST_ERROR,
  type BillScoutRecordedTransactionsRequestError,
} from './billScoutRecordedTransactionsTypes';

const ServerErrorDetailSchema = z
  .object({
    code: z.string().optional(),
    detail: z.string().optional(),
    errorCode: z.string().optional(),
    message: z.string().optional(),
    statusCode: z.string().optional(),
    title: z.string().optional(),
  })
  .passthrough();

const ServerErrorSchema = ServerErrorDetailSchema.extend({
  errors: z.array(ServerErrorDetailSchema).optional(),
});

const getValidationErrorDetails = (error: z.ZodError): { code: string; message: string } => ({
  code: 'INVALID_RESPONSE',
  message: error.issues
    .map((issue) => `${issue.path.length > 0 ? issue.path.join('.') : 'response'}: ${issue.message}`)
    .join('; '),
});

const getServerErrorDetails = (data: unknown): { code?: string; message?: string } => {
  const result = ServerErrorSchema.safeParse(data);
  if (!result.success) return {};

  const firstError = result.data.errors?.[0];
  const message =
    firstError?.detail ??
    firstError?.message ??
    firstError?.title ??
    result.data.detail ??
    result.data.message ??
    result.data.title;
  const code =
    firstError?.statusCode ??
    firstError?.errorCode ??
    firstError?.code ??
    result.data.statusCode ??
    result.data.errorCode ??
    result.data.code;

  return {
    ...(code ? { code } : {}),
    ...(message ? { message } : {}),
  };
};

export const toBillScoutRecordedTransactionsRequestError = (
  error: unknown,
  fallbackMessage = 'Recorded transaction request failed.',
): BillScoutRecordedTransactionsRequestError => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const serverError =
    error instanceof z.ZodError
      ? getValidationErrorDetails(error)
      : axios.isAxiosError(error)
        ? getServerErrorDetails(error.response?.data)
        : getServerErrorDetails(error);
  const message = serverError.message ?? (error instanceof Error ? error.message : fallbackMessage);

  return {
    type: BILL_SCOUT_RECORDED_TRANSACTIONS_REQUEST_ERROR,
    ambiguous: status === undefined || status === 408 || status >= 500,
    message,
    ...(status !== undefined ? { status } : {}),
    ...(serverError.code ? { code: serverError.code } : {}),
  };
};
